import { distance } from './selection/network';
import type { DevelopmentPlan } from './plans.types';
import type { LngLat, PlanBuilding } from './types';

type FC = GeoJSON.FeatureCollection;

/** Graine stable par plan, pour que chaque plan garde la même grappe de bâtiments. */
const seedOf = (id: string) => [...id].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);

/**
 * Bâtiments raccordés du plan. Le plan ne stocke que leur nombre : on retient, de façon déterministe,
 * les `buildingCount` bâtiments les plus proches d'un bâtiment de départ propre au plan.
 */
export function planBuildings(plan: DevelopmentPlan, buildings: PlanBuilding[]): PlanBuilding[] {
  if (plan.buildingCount <= 0 || buildings.length === 0) return [];
  const start = buildings[seedOf(plan.id) % buildings.length].centroid;
  return [...buildings]
    .sort((a, b) => distance(start, a.centroid) - distance(start, b.centroid) || a.num - b.num)
    .slice(0, plan.buildingCount);
}

/** Tracé de démonstration déterministe O(n log n), non optimal : parcours par bandes spatiales. */
export function planRoute(buildings: PlanBuilding[]): [LngLat, LngLat][] {
  const pts = buildings.map((b) => b.centroid);
  if (pts.length < 2) return [];
  const [minX, maxX] = pts.reduce(([min, max], p) => [Math.min(min, p[0]), Math.max(max, p[0])], [Infinity, -Infinity]);
  const width = (maxX - minX) / Math.ceil(Math.sqrt(pts.length)) || 1;
  const band = (p: LngLat) => Math.floor((p[0] - minX) / width);
  pts.sort((a, b) => band(a) - band(b)
    || (band(a) % 2 ? b[1] - a[1] : a[1] - b[1]) || a[0] - b[0]);
  return pts.slice(1).map((p, i) => [pts[i], p]);
}

export function routeFC(edges: [LngLat, LngLat][]): FC {
  return {
    type: 'FeatureCollection',
    features: edges.map((e, i) => ({ type: 'Feature', id: i + 1, properties: { id: i + 1 }, geometry: { type: 'LineString', coordinates: e } })),
  };
}
